import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Breadcrumbs, Card, CardContent } from '@mui/material';
import {
  Assessment as AssessmentIcon,
  NavigateNext as NavigateNextIcon,
  Warning as WarningIcon,
  PersonOff as PersonOffIcon,
  EventBusy as EventBusyIcon,
  EventAvailable as EventAvailableIcon,
  DirectionsCar as DirectionsCarIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import Button from '../../../components/Button';
import DateRangePicker from '../../../components/DateRangePicker';
import ExportButton from '../../../components/ExportButton';
import { useNotification } from '../../../components/Notification';
import Select from '../../../components/Select';
import Table from '../../../components/Table';
import { formatDateWithCulture } from '../../../utils/dateUtils';
import {
  fetchInit,
  fetchViolationReport,
  exportViolationReportToExcel,
  exportViolationReportToPdf,
  selectViolationReportData,
  selectViolationReportUnits,
  selectViolationReportLoading,
  selectViolationReportExportLoading,
  selectViolationReportPdfExportLoading,
  selectViolationReportError,
  selectViolationReportSuccessMessage,
  selectViolationReportFilterUnitId,
  selectViolationReportFilterFromDate,
  selectViolationReportFilterToDate,
  setFilterUnitId,
  setFilterFromDate,
  setFilterToDate,
  clearFilters,
  clearError,
  clearSuccessMessage,
} from './slice';

function ViolationStatistics() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const reportData = useSelector(selectViolationReportData);
  const units = useSelector(selectViolationReportUnits);
  const loading = useSelector(selectViolationReportLoading);
  const exportLoading = useSelector(selectViolationReportExportLoading);
  const pdfExportLoading = useSelector(selectViolationReportPdfExportLoading);
  const error = useSelector(selectViolationReportError);
  const successMessage = useSelector(selectViolationReportSuccessMessage);
  const filterUnitId = useSelector(selectViolationReportFilterUnitId);
  const filterFromDate = useSelector(selectViolationReportFilterFromDate);
  const filterToDate = useSelector(selectViolationReportFilterToDate);

  // Helper to check if value is empty (null, undefined, or empty string - NOT 0)
  const isEmpty = (value) => value === '' || value === null || value === undefined;

  // Fetch init data on mount
  useEffect(() => {
    dispatch(fetchInit());
  }, [dispatch]);

  // Auto-select first unit and today's date after init completes
  useEffect(() => {
    if (units.length > 0 && isEmpty(filterUnitId)) {
      dispatch(setFilterUnitId(units[0].id));
    }
    if (isEmpty(filterFromDate)) {
      const today = new Date().toISOString().split('T')[0];
      dispatch(setFilterFromDate(today));
    }
    if (isEmpty(filterToDate)) {
      const today = new Date().toISOString().split('T')[0];
      dispatch(setFilterToDate(today));
    }
  }, [units, filterUnitId, filterFromDate, filterToDate, dispatch]);

  // Show error notification
  useEffect(() => {
    if (error) {
      showNotification(t(error), 'error');
      dispatch(clearError());
    }
  }, [error, dispatch, showNotification, t]);

  // Show success notification
  useEffect(() => {
    if (successMessage) {
      showNotification(t(successMessage), 'success');
      dispatch(clearSuccessMessage());
    }
  }, [successMessage, dispatch, showNotification, t]);

  const handleSearch = () => {
    if (isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)) {
      showNotification(t('reports.violation.errors.selectFilters'), 'warning');
      return;
    }
    dispatch(fetchViolationReport());
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  const handleExportExcel = () => {
    if (isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)) {
      showNotification(t('reports.violation.errors.selectFilters'), 'warning');
      return;
    }
    dispatch(exportViolationReportToExcel());
  };

  const handleExportPdf = () => {
    if (isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)) {
      showNotification(t('reports.violation.errors.selectFilters'), 'warning');
      return;
    }
    dispatch(exportViolationReportToPdf());
  };

  // Define dashboard statistics data (5 boxes)
  // Line 1: 2 boxes (50% width each)
  // Line 2: 3 boxes (33.33% width each)
  const dashboardStatsLine1 = reportData
    ? [
        {
          key: 'totalExpirationDate',
          label: t('reports.violation.dashboard.totalExpirationDate'),
          value: reportData.totalExpirationDate || 0,
          icon: <EventBusyIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#f57c00',
          bgColor: '#fff3e0',
        },
        {
          key: 'totalEffectiveDateNotStarted',
          label: t('reports.violation.dashboard.totalEffectiveDateNotStarted'),
          value: reportData.totalEffectiveDateNotStarted || 0,
          icon: <EventAvailableIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#fbc02d',
          bgColor: '#fffde7',
        },
      ]
    : null;

  const dashboardStatsLine2 = reportData
    ? [
        {
          key: 'totalUnRegisteredID',
          label: t('reports.violation.dashboard.totalUnRegisteredID'),
          value: reportData.totalUnRegisteredID || 0,
          icon: <PersonOffIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#d32f2f',
          bgColor: '#ffebee',
        },
        {
          key: 'totalUnknownPerson',
          label: t('reports.violation.dashboard.totalUnknownPerson'),
          value: reportData.totalUnknownPerson || 0,
          icon: <WarningIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#c2185b',
          bgColor: '#fce4ec',
        },
        {
          key: 'totalUnregisteredVehicle',
          label: t('reports.violation.dashboard.totalUnregisteredVehicle'),
          value: reportData.totalUnregisteredVehicle || 0,
          icon: <DirectionsCarIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#7b1fa2',
          bgColor: '#f3e5f5',
        },
      ]
    : null;

  // Table columns
  const columns = [
    {
      key: 'deviceAddress',
      label: t('reports.violation.table.deviceAddress'),
    },
    {
      key: 'deviceName',
      label: t('reports.violation.table.deviceName'),
    },
    {
      key: 'deviceType',
      label: t('reports.violation.table.deviceType'),
    },
    {
      key: 'status',
      label: t('reports.violation.table.status'),
      render: (_value, row) => {
        const total =
          (row.totalUnRegisteredID || 0) +
          (row.totalExpirationDate || 0) +
          (row.totalEffectiveDateNotStarted || 0) +
          (row.totalUnregisteredVehicle || 0) +
          (row.totalUnknownPerson || 0);

        // Determine status level: mild (0-50), moderate (51-100), severe (>100)
        let status, bgColor, textColor, icon;
        if (total <= 50) {
          status = 'mild';
          bgColor = '#e8f5e9';
          textColor = '#2e7d32';
          icon = <CheckCircleIcon sx={{ fontSize: 18 }} />;
        } else if (total <= 100) {
          status = 'moderate';
          bgColor = '#fff3e0';
          textColor = '#f57c00';
          icon = <WarningIcon sx={{ fontSize: 18 }} />;
        } else {
          status = 'severe';
          bgColor = '#ffebee';
          textColor = '#d32f2f';
          icon = <WarningIcon sx={{ fontSize: 18 }} />;
        }

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: bgColor,
              color: textColor,
              width: 'fit-content',
            }}
          >
            {icon}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
              }}
            >
              {t(`reports.violation.status.${status}`)}
            </Typography>
          </Box>
        );
      },
    },
    {
      key: 'totalUnRegisteredID',
      label: t('reports.violation.table.totalUnRegisteredID'),
    },
    {
      key: 'totalExpirationDate',
      label: t('reports.violation.table.totalExpirationDate'),
    },
    {
      key: 'totalEffectiveDateNotStarted',
      label: t('reports.violation.table.totalEffectiveDateNotStarted'),
    },
    {
      key: 'totalUnregisteredVehicle',
      label: t('reports.violation.table.totalUnregisteredVehicle'),
    },
    {
      key: 'totalUnknownPerson',
      label: t('reports.violation.table.totalUnknownPerson'),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AssessmentIcon fontSize="small" />
            <Typography variant="body2">{t('sidebar.module6.title')}</Typography>
          </Box>
          <Typography variant="body2" color="text.primary">
            {t('sidebar.module6.violationStatistics')}
          </Typography>
        </Breadcrumbs>

        {/* Page Title */}
        <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left', mb: 3 }}>
          {t('reports.violation.title')}
        </Typography>

        {/* Filter Section */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <Box sx={{ minWidth: 250 }}>
            <Select
              label={t('reports.violation.filters.unit')}
              value={filterUnitId}
              onChange={(e) => dispatch(setFilterUnitId(e.target.value))}
              options={units}
              id="filterUnitId"
              name="filterUnitId"
              data-testid="violationReport-select-filterUnitId"
              fullWidth={true}
              required
            />
          </Box>
          <Box sx={{ minWidth: 300 }}>
            <DateRangePicker
              label={t('reports.violation.filters.dateRange')}
              fromValue={filterFromDate}
              toValue={filterToDate}
              onFromChange={(e) => dispatch(setFilterFromDate(e.target.value))}
              onToChange={(e) => dispatch(setFilterToDate(e.target.value))}
              id="filterDateRange"
              name="filterDateRange"
              data-testid="violationReport-daterangepicker-filter"
            />
          </Box>
          <Button
            variant="primary"
            icon="fas fa-search"
            iconPosition="left"
            onClick={handleSearch}
            disabled={loading || isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)}
            id="search"
            name="search"
            data-testid="violationReport-button-search"
            size="lg"
          >
            {t('reports.violation.search')}
          </Button>
          <ExportButton
            loading={exportLoading}
            disabled={isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)}
            onClick={handleExportExcel}
            id="exportExcel"
            name="exportExcel"
            data-testid="violationReport-button-exportExcel"
          />
          <ExportButton
            loading={pdfExportLoading}
            disabled={isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)}
            onClick={handleExportPdf}
            id="exportPdf"
            name="exportPdf"
            data-testid="violationReport-button-exportPdf"
            type="pdf"
          />
          <Button
            variant="outline"
            icon="close"
            iconPosition="left"
            onClick={handleClearFilters}
            disabled={loading}
            id="clearFilters"
            name="clearFilters"
            data-testid="violationReport-button-clearFilters"
            size="lg"
          >
            {t('reports.filters.clear')}
          </Button>
        </Box>
      </Box>

      {/* Dashboard Section */}
      {reportData && dashboardStatsLine1 && dashboardStatsLine2 && (
        <Box sx={{ px: 3, py: 2, backgroundColor: '#f5f5f5', flex: 1, overflow: 'auto' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t('reports.violation.dashboard.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {(() => {
              const selectedUnit = units.find((u) => u.id === Number(filterUnitId));
              const unitName = selectedUnit?.name || reportData.unitName || filterUnitId;
              const formattedFromDate = formatDateWithCulture(filterFromDate, i18n.language);
              const formattedToDate = formatDateWithCulture(filterToDate, i18n.language);
              return t('reports.violation.dashboard.subtitleRange', { unit: unitName, fromDate: formattedFromDate, toDate: formattedToDate });
            })()}
          </Typography>

          {/* Statistics Cards Line 1 - 2 boxes at 50% width */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            {dashboardStatsLine1.map((stat) => (
              <Box
                key={stat.key}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 100%', md: '1 1 calc(50% - 8px)' },
                  minWidth: { xs: '100%', sm: '250px' },
                }}
              >
                <Card
                  sx={{
                    backgroundColor: stat.bgColor,
                    borderLeft: `4px solid ${stat.color}`,
                    height: '100%',
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', flex: 1, textAlign: 'left' }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1, lineHeight: 1.4, minHeight: '40px', display: 'flex', alignItems: 'center' }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography variant="h4" component="div" sx={{ fontWeight: 700, lineHeight: 1.2, color: stat.color }}>
                          {stat.value.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ color: stat.color, ml: 2, flexShrink: 0 }}>{stat.icon}</Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          {/* Statistics Cards Line 2 - 3 boxes at 33.33% width */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            {dashboardStatsLine2.map((stat) => (
              <Box
                key={stat.key}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 100%', md: '1 1 calc(33.333% - 11px)' },
                  minWidth: { xs: '100%', sm: '250px' },
                }}
              >
                <Card
                  sx={{
                    backgroundColor: stat.bgColor,
                    borderLeft: `4px solid ${stat.color}`,
                    height: '100%',
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', flex: 1, textAlign: 'left' }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1, lineHeight: 1.4, minHeight: '40px', display: 'flex', alignItems: 'center' }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography variant="h4" component="div" sx={{ fontWeight: 700, lineHeight: 1.2, color: stat.color }}>
                          {stat.value.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ color: stat.color, ml: 2, flexShrink: 0 }}>{stat.icon}</Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          {/* Device Table */}
          {reportData.dataViolationInDay && reportData.dataViolationInDay.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t('reports.violation.table.title')}
              </Typography>
              <Table
                id="violationReport-table"
                columns={columns}
                data={reportData.dataViolationInDay}
                loading={loading}
                pagination={false}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Empty State */}
      {!reportData && !loading && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
          }}
        >
          <WarningIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('reports.violation.noData')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('reports.violation.selectFiltersToSearch')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default ViolationStatistics;
